import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody, TabContent, TabPane } from 'reactstrap';
import { useMutation } from '@apollo/client';
import { RECORD_DELETED_SUCCESSFULLY } from 'components/common/FSBToastify/types';
import { ADD_EDIT_CONSTANT } from 'utils/constants';
import { authContext } from 'contexts/AuthContext';
import { syncUserPermissions } from 'utils/permissions';
import FSBModal from 'components/common/FSBModal';
import CustomTabs from 'components/CustomTabs';
import Heading from 'components/Heading';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import DateRangeFilter from 'pages/Notifications/components/AllNotification/DateRangePicker';
import { DEACTIVATE_SUBSCRIPTION_PLAN, DELETE_TRADER } from './VendorGraphQL';
import { VENDOR_TYPES } from './constants';
import AddVendor from './components/AddVendor';
import IndianVendor from './components/IndianVendor';
import ForeignVendor from './components/ForeignVendor';
import SendEmail from './components/SendEmailToVendors/components/SendEmail';

const Vendor = () => {
  const { auth, setAuthData } = useContext(authContext);

  useEffect(() => {
    if (auth?.data?.token) {
      syncUserPermissions(auth.data, setAuthData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeTab, setActiveTab] = useState(VENDOR_TYPES.INDIAN);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [vendorType, setVendorType] = useState({
    type: ADD_EDIT_CONSTANT.ADD,
    value: '',
  });
  const [showAddRecord, setAddRecord] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState('');
  const [showDeleteRecord, setDeleteRecord] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState({
    status: false,
    id: '',
  });
  const [showDeactivatePlan, setShowDeactivatePlan] = useState({
    status: false,
    id: '',
  });

  const drawerEmailToggle = (id) => {
    setShowEmailPopup({ status: !showEmailPopup.status, id });
  };

  const [deleteTrader] = useMutation(DELETE_TRADER, {
    // eslint-disable-next-line no-shadow
    onCompleted: () => {
      toast.success(RECORD_DELETED_SUCCESSFULLY);
      setDeleteRowId('');
      setDeleteRecord(!showDeleteRecord);
    },

    onError: ({ graphQLErrors }) => {
      graphQLErrors.map(({ message }) => {
        return message;
      });
    },
  });

  const [deactivateTraderSubscriptionPlans, { loading }] = useMutation(
    DEACTIVATE_SUBSCRIPTION_PLAN,
    {
      // eslint-disable-next-line no-shadow
      onCompleted: ({ deactivateTraderSubscriptionPlans }) => {
        if (deactivateTraderSubscriptionPlans?.status === 204)
          toast.error(deactivateTraderSubscriptionPlans?.message);
        if (deactivateTraderSubscriptionPlans?.status === 200)
          toast.success(deactivateTraderSubscriptionPlans?.message);
      },
      onError: ({ graphQLErrors }) => {
        graphQLErrors.map(({ exception }) => {
          toast.error(exception?.message);
          return exception;
        });
      },
    },
  );

  const toggleDeleteRecord = (id) => {
    setDeleteRecord(!showDeleteRecord);
    setDeleteRowId(id);
  };

  const toggleDeactivatePlan = (id) => {
    setShowDeactivatePlan({ status: !showDeactivatePlan?.status, id });
  };

  const onDeleteConfirmation = async () => {
    try {
      await deleteTrader({
        variables: {
          id: deleteRowId,
          deletedFrom: 'Admin',
          deletedBy: auth?.data?.userId,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const toggleAddRecord = () => {
    setAddRecord(!showAddRecord);
  };

  const drawerToggle = (value, type = ADD_EDIT_CONSTANT.ADD) => {
    setIsDrawerOpen(!isDrawerOpen);
    setVendorType({ type, value });
  };
  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleClickEdit = (data, edit) => {
    drawerToggle(data, edit);
  };

  const onDeactivatePlanConfirmation = async () => {
    try {
      await deactivateTraderSubscriptionPlans({
        variables: {
          traders: [showDeactivatePlan?.id],
        },
      });
    } catch (err) {
      toast.error(err);
    }
    toggleDeactivatePlan();
  };

  return (
    <div className="staff-page vendor-page">
      <div className="mb-3 top-head">
        <Heading title="Vendor" />
      </div>

      <DeleteConfirmationAlert
        open={showDeleteRecord}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        onConfirm={onDeleteConfirmation}
        onCancel={toggleDeleteRecord}
      />
      <DeleteConfirmationAlert
        open={showDeactivatePlan?.status}
        title="Deactivate"
        message="Do you really want to deactivate the plans?"
        cancelButtonLabel="Close"
        addButtonLabel="Deactivate"
        onConfirm={onDeactivatePlanConfirmation}
        onCancel={toggleDeactivatePlan}
        loading={loading}
      />
      <FSBModal
        open={showEmailPopup?.status}
        size="xl"
        title=""
        closeButtonLabel=""
        confirmButtonLabel=""
        message=""
        toggleAlert={drawerEmailToggle}
        onClose={drawerEmailToggle}
        onConfirmClick={drawerEmailToggle}
        modalClass="right-drawer right-drawer-details"
      >
        <SendEmail
          rowSelection={[showEmailPopup?.id]}
          drawerToggle={drawerEmailToggle}
        />
      </FSBModal>

      <FSBModal
        title={`${vendorType.type} Vendor`}
        open={isDrawerOpen}
        toggleAlert={drawerToggle}
        onClose={drawerToggle}
        message=""
        modalClass="right-drawer"
      >
        <AddVendor
          toggleAddRecord={toggleAddRecord}
          type={vendorType.type}
          value={vendorType.value}
          drawerToggle={drawerToggle}
        />
      </FSBModal>

      <Card className="border-0 card-box page-card-box ">
        <CardBody>
          <div className="vendor-tab-row">
            <CustomTabs
              tabs={[VENDOR_TYPES.INDIAN, VENDOR_TYPES.FOREIGN]}
              selectedTab={activeTab}
              onTabChange={toggle}
            />
            <div className="vendor-date-filter">
              <DateRangeFilter
                dateRange={dateRange}
                onFilter={setDateRange}
              />
            </div>
          </div>
          <TabContent activeTab={activeTab}>
            {activeTab === VENDOR_TYPES.INDIAN && (
              <TabPane tabId={VENDOR_TYPES.INDIAN}>
                <IndianVendor
                  handleClickEdit={handleClickEdit}
                  toggleDeleteRecord={toggleDeleteRecord}
                  toggleDeactivatePlan={toggleDeactivatePlan}
                  drawerEmailToggle={drawerEmailToggle}
                  dateRange={dateRange}
                />
              </TabPane>
            )}
            {activeTab === VENDOR_TYPES.FOREIGN && (
              <TabPane tabId={VENDOR_TYPES.FOREIGN}>
                <ForeignVendor
                  handleClickEdit={handleClickEdit}
                  toggleDeleteRecord={toggleDeleteRecord}
                  toggleDeactivatePlan={toggleDeactivatePlan}
                  drawerEmailToggle={drawerEmailToggle}
                  dateRange={dateRange}
                />
              </TabPane>
            )}
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default Vendor;
